import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import { useRegister } from '../hooks/useRegister'
import { registerSchema, type RegisterFormData, type RegisterFormInput } from '../types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import type { ApiErrorResponse } from '@/types/api'

export function RegisterForm() {
  const { t } = useTranslation()
  const { mutate: register, isPending, isSuccess } = useRegister()
  const [apiError, setApiError] = useState<string | null>(null)

  const {
    register: field,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<RegisterFormInput, unknown, RegisterFormData>({
    resolver: zodResolver(registerSchema),
  })

  if (isSuccess) {
    return <Alert variant="success">{t('auth.register_success')}</Alert>
  }

  const onSubmit = (data: RegisterFormData) => {
    setApiError(null)
    register(data, {
      onError: (err) => {
        if (axios.isAxiosError(err) && err.response?.data) {
          const body = err.response.data as ApiErrorResponse
          const firstError = body.errors?.[0]
          if (firstError?.field === 'email') {
            setError('email', { message: firstError.message })
          } else {
            setApiError(firstError?.message ?? t('auth.errors.generic'))
          }
        }
      },
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
      {apiError && <Alert variant="error">{apiError}</Alert>}

      <div className="grid grid-cols-2 gap-3">
        <Input
          label={t('auth.first_name')}
          type="text"
          autoComplete="given-name"
          error={errors.first_name?.message && t(errors.first_name.message)}
          {...field('first_name')}
        />
        <Input
          label={t('auth.last_name')}
          type="text"
          autoComplete="family-name"
          error={errors.last_name?.message && t(errors.last_name.message)}
          {...field('last_name')}
        />
      </div>

      <Input
        label={t('auth.email')}
        type="email"
        autoComplete="email"
        placeholder={t('auth.email_placeholder')}
        error={errors.email?.message && t(errors.email.message)}
        {...field('email')}
      />

      <Input
        label={t('auth.password')}
        type="password"
        autoComplete="new-password"
        helperText={t('auth.password_hint')}
        error={errors.password?.message && t(errors.password.message)}
        {...field('password')}
      />

      <Input
        label={t('auth.password_confirmation')}
        type="password"
        autoComplete="new-password"
        error={
          errors.password_confirmation?.message && t(errors.password_confirmation.message)
        }
        {...field('password_confirmation')}
      />

      <Button type="submit" isLoading={isPending} className="w-full">
        {t('auth.register_submit')}
      </Button>

      {/* L'opacité réduite ne porte que sur le texte statique : appliquée au
          <p> entier, elle diluait aussi le lien et faisait tomber son
          contraste sous le seuil AA (3,46:1 mesuré au lieu de 4,5:1). */}
      <p className="text-center text-sm text-surface-800">
        <span className="opacity-70">{t('auth.already_account')}</span>{' '}
        <Link to="/auth/login" className="font-medium text-primary-600 hover:underline">
          {t('auth.login')}
        </Link>
      </p>
    </form>
  )
}
